import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PublishIcon from '@mui/icons-material/Publish';
import SearchIcon from '@mui/icons-material/Search';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';

import {
    fetchQuizzes,
    deleteQuiz,
    publishQuiz,
} from '../vsecuriteSlice';
import { showSuccessToast, showErrorToast } from '../../../app/utils/helpers';
import Langs, { Localize } from '../../../app/lang/langs';
import ConfirmDialog from '../../popups/ConfirmDialog';

/** Brand orange used throughout this module */
const BRAND_ORANGE = '#FF9015';
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * Maps a quiz status value to its display label and chip color.
 * @param {string} status - Raw status string from the API.
 * @param {string} lang   - Current language code.
 * @returns {{ label: string, color: string }} Display label and MUI Chip color.
 */
const resolveStatus = (status, lang) => {
    switch (status) {
        case 'published':
            return { label: Localize('VSEC_STATUS_PUBLISHED_CHIP', lang), color: 'success' };
        case 'draft':
            return { label: Localize('VSEC_STATUS_DRAFT_CHIP', lang), color: 'warning' };
        case 'archived':
            return { label: Localize('VSEC_STATUS_ARCHIVED_CHIP', lang), color: 'default' };
        default:
            return { label: status || Localize('VSEC_STATUS_UNKNOWN', lang), color: 'default' };
    }
};

/**
 * QuizList – Admin view listing all V-Sécurité quizzes in a MUI DataGrid.
 *
 * @param {object}   props
 * @param {Function} props.onSelectQuiz       - Called with the quiz row when "Tracking" is clicked.
 * @param {Function} props.onEditQuiz         - Called with the quiz row when "Modifier" is clicked.
 * @param {Function} props.onCreateNew        - Called when "Nouveau Quiz" button is clicked.
 * @param {Function} props.onManageQuestions  - Called with the quiz row when "Questions" is clicked.
 */
const QuizList = ({ onSelectQuiz, onEditQuiz, onCreateNew, onManageQuestions }) => {
    const dispatch = useDispatch();
    const mounted = useRef(false);
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

    /* ── Redux state ─────────────────────────────────────────────────────────── */
    const quizList = useSelector((state) => state.vsecurite.quizzes.list);
    const loading  = useSelector((state) => state.vsecurite.quizzes.loading);
    const lang     = useSelector((state) => state.settings.lang);
    const isMobile = useSelector((state) => state.settings.isMobile);
    const mobile = isMobile === 2 || isMobileScreen;

    /* ── Local UI state ──────────────────────────────────────────────────────── */
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [statusFilter,      setStatusFilter]      = useState('');
    const [scoreRange,        setScoreRange]         = useState([0, 100]);
    const [nbQMin,            setNbQMin]             = useState('');
    const [nbQMax,            setNbQMax]             = useState('');
    const [dateFrom,          setDateFrom]           = useState('');
    const [dateTo,            setDateTo]             = useState('');
    const [showMobileFilters, setShowMobileFilters]  = useState(false);

    /** Quiz targeted by a pending delete or publish confirmation dialog. */
    const [confirmTarget, setConfirmTarget] = useState(null);
    /** Which confirmation dialog is currently open: 'delete' | 'publish' | null */
    const [confirmType, setConfirmType]     = useState(null);
    /** Tracks in-flight delete/publish requests to show per-row loading feedback. */
    const [actionLoading, setActionLoading] = useState(false);

    /* ── On mount: load quiz list ────────────────────────────────────────────── */
    useEffect(() => {
        if (!mounted.current) {
            dispatch(fetchQuizzes());
        }
        mounted.current = true;
    }, [dispatch]);

    /* ── Client-side filters ─────────────────────────────────────────────────── */
    const filteredRows = useMemo(() => {
        if (!Array.isArray(quizList)) return [];
        return quizList.filter((row) => {
            // Text search
            const q = globalFilterValue.trim().toLowerCase();
            if (q) {
                const title  = (row.title  || '').toLowerCase();
                const status = (row.statut || row.status || '').toLowerCase();
                if (!title.includes(q) && !status.includes(q)) return false;
            }
            // Status dropdown
            if (statusFilter) {
                const s = row.statut || row.status || '';
                if (s !== statusFilter) return false;
            }
            // Min score range slider
            const score = row.min_score ?? row.passing_score ?? row.score_minimum;
            if (score != null) {
                if (score < scoreRange[0] || score > scoreRange[1]) return false;
            }
            // Nb questions min/max
            const nbQ = row.question_count ?? row.nb_questions ?? row.questions_count ?? (Array.isArray(row.questions) ? row.questions.length : null);
            if (nbQ != null) {
                if (nbQMin !== '' && nbQ < Number(nbQMin)) return false;
                if (nbQMax !== '' && nbQ > Number(nbQMax)) return false;
            }
            // Publication date range
            const pubVal = row.date_publication || row.published_at;
            if (pubVal) {
                const pubDate = moment(pubVal);
                if (dateFrom && pubDate.isBefore(moment(dateFrom), 'day')) return false;
                if (dateTo   && pubDate.isAfter(moment(dateTo),   'day')) return false;
            }
            return true;
        });
    }, [quizList, globalFilterValue, statusFilter, scoreRange, nbQMin, nbQMax, dateFrom, dateTo]);

    /* ── Filter helpers ──────────────────────────────────────────────────────── */

    /**
     * Updates the global text filter value.
     * @param {React.ChangeEvent<HTMLInputElement>} e
     */
    const onGlobalFilterChange = (e) => {
        setGlobalFilterValue(e.target.value);
    };

    const resetAllFilters = () => {
        setStatusFilter('');
        setScoreRange([0, 100]);
        setNbQMin('');
        setNbQMax('');
        setDateFrom('');
        setDateTo('');
        setGlobalFilterValue('');
    };

    /* ── Confirmation dialog helpers ─────────────────────────────────────────── */

    /**
     * Opens the delete confirmation dialog for a given quiz row.
     * @param {object} quiz - The quiz row data.
     */
    const openDeleteConfirm = (quiz) => {
        setConfirmTarget(quiz);
        setConfirmType('delete');
    };

    /**
     * Opens the publish confirmation dialog for a given quiz row.
     * @param {object} quiz - The quiz row data.
     */
    const openPublishConfirm = (quiz) => {
        setConfirmTarget(quiz);
        setConfirmType('publish');
    };

    /** Dismisses any open confirmation dialog and clears its target. */
    const closeConfirm = () => {
        setConfirmTarget(null);
        setConfirmType(null);
    };

    /* ── Action handlers ─────────────────────────────────────────────────────── */

    /**
     * Executes the delete action for confirmTarget, then refreshes the list.
     */
    const handleConfirmDelete = async () => {
        if (!confirmTarget) return;
        setActionLoading(true);
        try {
            await dispatch(deleteQuiz({ id: confirmTarget.id })).unwrap();
            showSuccessToast(Localize('VSEC_TOAST_QUIZ_DELETED', lang));
            dispatch(fetchQuizzes());
        } catch {
            showErrorToast(Localize('VSEC_TOAST_QUIZ_DELETE_ERROR', lang));
        } finally {
            setActionLoading(false);
            closeConfirm();
        }
    };

    /**
     * Executes the publish action for confirmTarget, then refreshes the list.
     */
    const handleConfirmPublish = async () => {
        if (!confirmTarget) return;
        setActionLoading(true);
        try {
            await dispatch(publishQuiz({ id: confirmTarget.id })).unwrap();
            showSuccessToast(Localize('VSEC_TOAST_QUIZ_PUBLISHED', lang));
            dispatch(fetchQuizzes());
        } catch {
            showErrorToast(Localize('VSEC_TOAST_QUIZ_PUBLISH_ERROR', lang));
        } finally {
            setActionLoading(false);
            closeConfirm();
        }
    };

    /* ── DataGrid column definitions ─────────────────────────────────────────── */
    const columns = useMemo(() => [
        {
            field: 'title',
            headerName: Localize('VSEC_COL_TITLE', lang),
            minWidth: 200,
            flex: 2,
            sortable: true,
        },
        {
            field: 'min_score',
            headerName: Localize('VSEC_COL_MIN_SCORE', lang),
            minWidth: 140,
            flex: 1,
            sortable: true,
            renderCell: (params) => {
                const row   = params.row;
                const score = row.min_score ?? row.passing_score ?? row.score_minimum;
                return score != null ? `${score} %` : <em style={{ color: '#aaa' }}>—</em>;
            },
        },
        {
            field: 'status',
            headerName: Localize('VSEC_COL_STATUS', lang),
            minWidth: 130,
            flex: 1,
            sortable: true,
            renderCell: (params) => {
                const row = params.row;
                const { label, color } = resolveStatus(row.statut || row.status, lang);
                return (
                    <Chip
                        label={label}
                        color={color}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                );
            },
        },
        {
            field: 'nb_questions',
            headerName: Localize('VSEC_COL_NB_QUESTIONS', lang),
            minWidth: 130,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const row   = params.row;
                const count =
                    row.question_count ??
                    row.nb_questions ??
                    row.questions_count ??
                    (Array.isArray(row.questions) ? row.questions.length : null);
                return count != null ? count : <em style={{ color: '#aaa' }}>—</em>;
            },
        },
        {
            field: 'published_at',
            headerName: Localize('VSEC_COL_PUBLISH_DATE', lang),
            minWidth: 140,
            flex: 1,
            sortable: true,
            renderCell: (params) => {
                const row = params.row;
                const val = row.date_publication || row.published_at;
                return val
                    ? moment(val).format('DD-MM-YYYY')
                    : <em style={{ color: '#aaa' }}>—</em>;
            },
        },
        {
            field: '__actions__',
            headerName: Localize('VSEC_COL_ACTIONS', lang),
            minWidth: 180,
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row       = params.row;
                const status    = row.statut || row.status;
                const isDraft   = status === 'draft';
                const isPublished = status === 'published';

                return (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {/* Questions – always available */}
                        <Tooltip title={Localize('VSEC_TOOLTIP_MANAGE_QUESTIONS', lang)}>
                            <IconButton
                                size="small"
                                onClick={() => onManageQuestions(row)}
                                sx={{ color: '#555' }}
                            >
                                <ListAltIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {/* Tracking – only if published */}
                        {isPublished && (
                            <Tooltip title={Localize('VSEC_TOOLTIP_TRACKING', lang)}>
                                <IconButton
                                    size="small"
                                    onClick={() => onSelectQuiz(row)}
                                    sx={{ color: BRAND_ORANGE }}
                                >
                                    <TrackChangesIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Settings – only if published */}
                        {isPublished && (
                            <Tooltip title={Localize('VSEC_TOOLTIP_QUIZ_SETTINGS', lang)}>
                                <IconButton
                                    size="small"
                                    onClick={() => onEditQuiz(row)}
                                    sx={{ color: '#1976d2' }}
                                >
                                    <SettingsOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Modifier – only if draft */}
                        {isDraft && (
                            <Tooltip title={Localize('VSEC_TOOLTIP_EDIT', lang)}>
                                <IconButton
                                    size="small"
                                    onClick={() => onEditQuiz(row)}
                                    sx={{ color: '#1976d2' }}
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Publier – only if draft */}
                        {isDraft && (
                            <Tooltip title={Localize('VSEC_TOOLTIP_PUBLISH_QUIZ', lang)}>
                                <IconButton
                                    size="small"
                                    onClick={() => openPublishConfirm(row)}
                                    sx={{ color: '#2e7d32' }}
                                >
                                    <PublishIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Supprimer – always available */}
                        <Tooltip title={Localize('VSEC_TOOLTIP_DELETE_QUIZ', lang)}>
                            <IconButton
                                size="small"
                                onClick={() => openDeleteConfirm(row)}
                                sx={{ color: '#d32f2f' }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [lang, onManageQuestions, onSelectQuiz, onEditQuiz]);

    /* ── Mobile card renderer ─────────────────────────────────────────────────── */
    const renderMobileCard = (row) => {
        const status    = row.statut || row.status;
        const isDraft   = status === 'draft';
        const isPublished = status === 'published';
        const { label, color } = resolveStatus(status, lang);
        const score = row.min_score ?? row.passing_score ?? row.score_minimum;
        const nbQ = row.question_count ?? row.nb_questions ?? row.questions_count ?? (Array.isArray(row.questions) ? row.questions.length : null);
        const pubVal = row.date_publication || row.published_at;

        return (
            <Box
                key={row.id}
                sx={{
                    background: '#fff',
                    borderRadius: '14px',
                    border: '1px solid rgba(0,40,128,0.06)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    mb: 1.5,
                    overflow: 'hidden',
                    transition: `all 200ms ${EASE}`,
                    '&:active': { transform: 'scale(0.99)' },
                }}
            >
                {/* Card header */}
                <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontWeight: 650, fontSize: '0.92rem', color: '#1a1a2e', flex: 1, lineHeight: 1.3, wordBreak: 'break-word' }}>
                            {row.title}
                        </Typography>
                        <Chip label={label} color={color} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', flexShrink: 0 }} />
                    </Box>

                    {/* Meta row */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1, color: '#777', fontSize: '0.8rem' }}>
                        {score != null && (
                            <Typography variant="caption" sx={{ color: '#666' }}>
                                Score min: <strong>{score}%</strong>
                            </Typography>
                        )}
                        {nbQ != null && (
                            <Typography variant="caption" sx={{ color: '#666' }}>
                                Questions: <strong>{nbQ}</strong>
                            </Typography>
                        )}
                        {pubVal && (
                            <Typography variant="caption" sx={{ color: '#666' }}>
                                {moment(pubVal).format('DD/MM/YYYY')}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Card actions */}
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.25, gap: 0.25 }}>
                    <IconButton
                        size="small"
                        onClick={() => onManageQuestions(row)}
                        sx={{ color: '#555', minWidth: 36, minHeight: 36 }}
                    >
                        <ListAltIcon sx={{ fontSize: '1.15rem' }} />
                    </IconButton>

                    {isPublished && (
                        <IconButton
                            size="small"
                            onClick={() => onSelectQuiz(row)}
                            sx={{ color: BRAND_ORANGE, minWidth: 36, minHeight: 36 }}
                        >
                            <TrackChangesIcon sx={{ fontSize: '1.15rem' }} />
                        </IconButton>
                    )}

                    {isPublished && (
                        <IconButton
                            size="small"
                            onClick={() => onEditQuiz(row)}
                            sx={{ color: '#1976d2', minWidth: 36, minHeight: 36 }}
                        >
                            <SettingsOutlinedIcon sx={{ fontSize: '1.15rem' }} />
                        </IconButton>
                    )}

                    {isDraft && (
                        <IconButton
                            size="small"
                            onClick={() => onEditQuiz(row)}
                            sx={{ color: '#1976d2', minWidth: 36, minHeight: 36 }}
                        >
                            <EditOutlinedIcon sx={{ fontSize: '1.15rem' }} />
                        </IconButton>
                    )}

                    {isDraft && (
                        <IconButton
                            size="small"
                            onClick={() => openPublishConfirm(row)}
                            sx={{ color: '#2e7d32', minWidth: 36, minHeight: 36 }}
                        >
                            <PublishIcon sx={{ fontSize: '1.15rem' }} />
                        </IconButton>
                    )}

                    <IconButton
                        size="small"
                        onClick={() => openDeleteConfirm(row)}
                        sx={{ color: '#d32f2f', minWidth: 36, minHeight: 36 }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: '1.15rem' }} />
                    </IconButton>
                </Box>
            </Box>
        );
    };

    /* ── Render ──────────────────────────────────────────────────────────────── */
    return (
        <Box sx={{ mt: 0, display: 'flex', flexDirection: 'column', height: mobile ? 'calc(100vh - 140px)' : 'auto' }}>
            {/* ── Top bar ─────────────────────────────────────────────────────── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    px: mobile ? 0.5 : 1,
                    flexWrap: mobile ? 'wrap' : 'nowrap',
                    gap: mobile ? 1 : 0,
                    flexShrink: 0,
                }}
            >
                <Typography variant={mobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, color: '#1a1a2e', letterSpacing: '-0.02em' }}>
                    <Langs str="VSEC_QUIZ_LIST_TITLE" />
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {mobile && (
                        <IconButton
                            onClick={() => setShowMobileFilters((p) => !p)}
                            sx={{
                                color: showMobileFilters ? BRAND_ORANGE : '#666',
                                border: '1px solid',
                                borderColor: showMobileFilters ? BRAND_ORANGE : 'rgba(0,0,0,0.12)',
                                borderRadius: '10px',
                                minWidth: 40,
                                minHeight: 40,
                            }}
                        >
                            <FilterListIcon />
                        </IconButton>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={onCreateNew}
                        sx={{
                            backgroundColor: BRAND_ORANGE,
                            color: '#fff',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(255,144,21,0.25)',
                            transition: `all 250ms ${EASE}`,
                            minHeight: 40,
                            width: mobile ? 'auto' : undefined,
                            '&:hover': { backgroundColor: '#e6820f', boxShadow: '0 6px 20px rgba(255,144,21,0.35)', transform: 'translateY(-1px) scale(1.02)' },
                            '&:active': { transform: 'scale(0.98)' },
                        }}
                    >
                        <Langs str="VSEC_BTN_NEW_QUIZ" />
                    </Button>
                </Box>
            </Box>

            {/* ── Mobile filters (collapsible) ────────────────────────────────── */}
            {mobile && (
                <Box sx={{ flexShrink: 0 }}>
                <Collapse in={showMobileFilters}>
                    <Box
                        sx={{
                            background: 'rgba(247,248,252,0.98)',
                            borderRadius: '14px',
                            border: '1px solid rgba(0,40,128,0.06)',
                            p: 2,
                            mb: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <TextField
                            size="small"
                            fullWidth
                            value={globalFilterValue}
                            onChange={onGlobalFilterChange}
                            placeholder={Localize('VSEC_SEARCH_PLACEHOLDER', lang)}
                            sx={{ '& input': { fontSize: '16px' } }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: '#aaa' }} />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <FormControl size="small" fullWidth>
                            <InputLabel>{Localize('VSEC_COL_STATUS', lang)}</InputLabel>
                            <Select
                                value={statusFilter}
                                label={Localize('VSEC_COL_STATUS', lang)}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                sx={{ fontSize: '16px' }}
                            >
                                <MenuItem value="">{Localize('VSEC_FILTER_ALL', lang)}</MenuItem>
                                <MenuItem value="draft">{Localize('VSEC_STATUS_DRAFT_CHIP', lang)}</MenuItem>
                                <MenuItem value="published">{Localize('VSEC_STATUS_PUBLISHED_CHIP', lang)}</MenuItem>
                                <MenuItem value="archived">{Localize('VSEC_STATUS_ARCHIVED_CHIP', lang)}</MenuItem>
                            </Select>
                        </FormControl>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(100,116,139,0.75)', display: 'block', mb: 0.5 }}>
                                Score: <Box component="span" sx={{ color: BRAND_ORANGE }}>{scoreRange[0]}%</Box>
                                {' — '}
                                <Box component="span" sx={{ color: BRAND_ORANGE }}>{scoreRange[1]}%</Box>
                            </Typography>
                            <Slider
                                value={scoreRange}
                                onChange={(_, val) => setScoreRange(val)}
                                min={0} max={100} step={5}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => `${v}%`}
                                sx={{
                                    color: BRAND_ORANGE,
                                    height: 4,
                                    '& .MuiSlider-thumb': { width: 20, height: 20 },
                                    '& .MuiSlider-valueLabel': { bgcolor: BRAND_ORANGE, fontSize: '0.68rem' },
                                }}
                            />
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={resetAllFilters}
                            sx={{
                                color: BRAND_ORANGE,
                                borderColor: 'rgba(255,144,21,0.4)',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '10px',
                                minHeight: 40,
                                '&:hover': { borderColor: BRAND_ORANGE, bgcolor: 'rgba(255,144,21,0.05)' },
                            }}
                        >
                            {Localize('VSEC_FILTER_RESET_BTN', lang)}
                        </Button>
                    </Box>
                </Collapse>
                </Box>
            )}

            {/* ── Always-visible mobile search (sticky, outside scroll area) ─── */}
            {mobile && !showMobileFilters && (
                <Box sx={{ flexShrink: 0 }}>
                    <TextField
                        size="small"
                        fullWidth
                        value={globalFilterValue}
                        onChange={onGlobalFilterChange}
                        placeholder={Localize('VSEC_SEARCH_PLACEHOLDER', lang)}
                        sx={{ mb: 2, '& input': { fontSize: '16px' } }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: '#aaa' }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Box>
            )}

            {/* ── DataGrid card (desktop) / Card list (mobile) ────────────────── */}
            {mobile ? (
                /* ── Mobile card list (scrollable) ── */
                <Box sx={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', minHeight: 0 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {[1, 2, 3].map((i) => (
                                <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: '14px' }} />
                            ))}
                        </Box>
                    ) : filteredRows.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, color: '#999' }}>
                            <Typography sx={{ fontWeight: 500 }}>
                                {Localize('VSEC_EMPTY_QUIZZES', lang)}
                            </Typography>
                        </Box>
                    ) : (
                        filteredRows.map((row) => renderMobileCard(row))
                    )}
                </Box>
            ) : (
                /* ── Desktop DataGrid ── */
                <Box
                    sx={{
                        background: 'rgba(255,255,255,0.75)',
                        backdropFilter: 'blur(14px)',
                        WebkitBackdropFilter: 'blur(14px)',
                        borderRadius: '18px',
                        border: '1px solid rgba(0,40,128,0.06)',
                        boxShadow: '0 1px 4px rgba(255,144,21,0.04), 0 8px 32px rgba(0,40,128,0.04)',
                        overflow: 'hidden',
                        transition: `box-shadow 300ms ${EASE}`,
                        '@keyframes qlFadeIn': {
                            from: { opacity: 0, transform: 'translateY(10px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                        },
                        animation: `qlFadeIn 400ms ${EASE} both`,
                    }}
                >
                    {/* ── Filter header row — one cell per column ───────────────── */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0,
                            pl: '23px',
                            pr: '10px',
                            py: 1.25,
                            background: 'rgba(247,248,252,0.98)',
                            borderBottom: '1px solid rgba(0,40,128,0.08)',
                        }}
                    >
                        {/* Title search */}
                        <Box sx={{ flex: 2, minWidth: 200, px: '10px' }}>
                            <TextField
                                size="small"
                                fullWidth
                                value={globalFilterValue}
                                onChange={onGlobalFilterChange}
                                placeholder={Localize('VSEC_SEARCH_PLACEHOLDER', lang)}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" sx={{ color: '#aaa' }} />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(0,40,128,0.1)' }} />

                        {/* Min score range slider */}
                        <Box sx={{ flex: 1, minWidth: 140, px: '10px' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'rgba(100,116,139,0.75)', display: 'block', mb: 0, lineHeight: 1.2 }}>
                                <Box component="span" sx={{ color: BRAND_ORANGE }}>{scoreRange[0]}%</Box>
                                {' — '}
                                <Box component="span" sx={{ color: BRAND_ORANGE }}>{scoreRange[1]}%</Box>
                            </Typography>
                            <Slider
                                value={scoreRange}
                                onChange={(_, val) => setScoreRange(val)}
                                min={0} max={100} step={5}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => `${v}%`}
                                sx={{
                                    color: BRAND_ORANGE,
                                    height: 4,
                                    py: '6px',
                                    '& .MuiSlider-thumb': { width: 12, height: 12 },
                                    '& .MuiSlider-valueLabel': { bgcolor: BRAND_ORANGE, fontSize: '0.68rem' },
                                    '& .MuiSlider-rail': { bgcolor: 'rgba(0,0,0,0.1)' },
                                }}
                            />
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(0,40,128,0.1)' }} />

                        {/* Status select */}
                        <Box sx={{ flex: 1, minWidth: 130, px: '10px' }}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>{Localize('VSEC_COL_STATUS', lang)}</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label={Localize('VSEC_COL_STATUS', lang)}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="">{Localize('VSEC_FILTER_ALL', lang)}</MenuItem>
                                    <MenuItem value="draft">{Localize('VSEC_STATUS_DRAFT_CHIP', lang)}</MenuItem>
                                    <MenuItem value="published">{Localize('VSEC_STATUS_PUBLISHED_CHIP', lang)}</MenuItem>
                                    <MenuItem value="archived">{Localize('VSEC_STATUS_ARCHIVED_CHIP', lang)}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(0,40,128,0.1)' }} />

                        {/* Reset */}
                        <Box sx={{ minWidth: 100, px: '10px', display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={resetAllFilters}
                                sx={{ color: BRAND_ORANGE, borderColor: 'rgba(255,144,21,0.4)', textTransform: 'none', fontWeight: 600, borderRadius: '8px', fontSize: '0.75rem', '&:hover': { borderColor: BRAND_ORANGE, bgcolor: 'rgba(255,144,21,0.05)' } }}
                            >
                                {Localize('VSEC_FILTER_RESET_BTN', lang)}
                            </Button>
                        </Box>
                    </Box>

                    {/* ── DataGrid ──────────────────────────────────────────────── */}
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        loading={loading}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        initialState={{
                            pagination: { paginationModel: { pageSize: 25 } },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        localeText={{
                            noRowsLabel: Localize('VSEC_EMPTY_QUIZZES', lang),
                        }}
                        sx={{
                            height: 'calc(100vh - 320px)',
                            borderRadius: 0,
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: 'rgba(247,248,252,0.9)',
                                fontWeight: 700,
                            },
                            '& .MuiDataGrid-row': {
                                transition: `background-color 200ms ${EASE}`,
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: 'rgba(255,144,21,0.05)',
                            },
                            '& .MuiDataGrid-row:nth-of-type(even)': {
                                backgroundColor: 'rgba(0,40,128,0.015)',
                            },
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                                borderBottom: '1px solid rgba(0,40,128,0.04)',
                            },
                        }}
                    />
                </Box>
            )}

            {/* ── Delete confirmation dialog ────────────────────────────────────── */}
            <ConfirmDialog
                open={confirmType === 'delete'}
                danger
                loading={actionLoading}
                title={<Langs str="VSEC_DELETE_QUIZ_TITLE" />}
                message={<>
                    {Localize('VSEC_DELETE_QUIZ_MSG', lang)}{' '}
                    <strong>{confirmTarget?.title}</strong>{' '}
                    {Localize('VSEC_DELETE_QUIZ_MSG2', lang)}
                </>}
                confirmLabel={<Langs str="VSEC_BTN_DELETE" />}
                cancelLabel={<Langs str="VSEC_BTN_CANCEL" />}
                onConfirm={handleConfirmDelete}
                onCancel={closeConfirm}
            />

            {/* ── Publish confirmation dialog ───────────────────────────────────── */}
            <ConfirmDialog
                open={confirmType === 'publish'}
                loading={actionLoading}
                title={<Langs str="VSEC_PUBLISH_QUIZ_TITLE" />}
                message={<>
                    {Localize('VSEC_PUBLISH_QUIZ_MSG', lang)}{' '}
                    <strong>{confirmTarget?.title}</strong>{' '}
                    {Localize('VSEC_PUBLISH_QUIZ_MSG2', lang)}
                </>}
                confirmLabel={<Langs str="VSEC_BTN_CONFIRM" />}
                cancelLabel={<Langs str="VSEC_BTN_CANCEL" />}
                onConfirm={handleConfirmPublish}
                onCancel={closeConfirm}
            />
        </Box>
    );
};

export default QuizList;
